'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import type React from 'react'
import { Suspense } from 'react'
import { AnalyticsProvider } from '@/components/providers/AnalyticsProvider'
import { ClickIdTracker } from '@/components/providers/ClickIdTracker'
import StrapiPreviewListener from '@/components/providers/StrapiPreviewListener'
import { env } from '@/env.mjs'
import { BugsnagBrowserClient, BugsnagPerformanceClient } from './BugsnagBrowserClient'

const enabledReleaseStages = ['production', 'staging', 'development']

const releaseStage = env.NEXT_PUBLIC_APP_ENV
const apiKey = env.NEXT_PUBLIC_BUGSNAG_API_KEY
const isEnabled = env.NEXT_PUBLIC_BUGSNAG_ENABLED

const isMonitoringEnabled = enabledReleaseStages.includes(releaseStage) && isEnabled && apiKey

const queryClient = new QueryClient()

export function ClientProviders({
	children,
	listenToStrapiUpdates,
}: {
	readonly children: React.ReactNode
	readonly listenToStrapiUpdates?: boolean
}) {
	return (
		<ThemeProvider attribute="class" defaultTheme="system" enableSystem forcedTheme="light">
			{isMonitoringEnabled ? (
				<>
					<BugsnagBrowserClient
						appType={env.NEXT_PUBLIC_BUGSNAG_APP_TYPE}
						apiKey={apiKey}
						appVersion={env.NEXT_PUBLIC_BUGSNAG_APP_VERSION}
						releaseStage={env.NEXT_PUBLIC_APP_ENV}
						enabledReleaseStages={enabledReleaseStages}
					/>
					<BugsnagPerformanceClient
						apiKey={apiKey}
						appVersion={env.NEXT_PUBLIC_BUGSNAG_APP_VERSION}
						releaseStage={env.NEXT_PUBLIC_APP_ENV}
						enabledReleaseStages={enabledReleaseStages}
					/>
				</>
			) : null}

			{listenToStrapiUpdates && <StrapiPreviewListener />}

			<QueryClientProvider client={queryClient}>
				<AnalyticsProvider amplitudeApiKey={env.NEXT_PUBLIC_AMPLITUDE_API_KEY}>
					{children}
				</AnalyticsProvider>
			</QueryClientProvider>

			<Suspense fallback={null}>
				<ClickIdTracker />
			</Suspense>
		</ThemeProvider>
	)
}
